import type { TranslationKey } from "../locales";
import type { Enums } from "../types";

/** The heading a kind is listed under wherever a person chooses what reaches them. */
export type NotificationGroup =
	| "notes"
	| "protocols"
	| "projects"
	| "orders"
	| "planning"
	| "attendances"
	| "substitutions"
	| "partners"
	| "incoming_invoices";

/**
 * One kind of notification: the event a person can be told about, and the channels it can reach
 * them on.
 *
 * `key` is the `notification_kind` value a push opt-out stores. `push` is the `notification_type`
 * the push's `data.type` carries and `email` the `email_type` the mail is sent, logged and
 * unsubscribed as; null where the kind has no such channel. `isExternal` marks a mail that can go
 * to an address outside the organisation — a client's contact person — which is what the
 * preferences page behind the unsubscribe link offers to someone who is not a user.
 */
export interface NotificationKind {
	key: Enums<"notificationKind">;
	group: NotificationGroup;
	label: TranslationKey;
	description: TranslationKey;
	push: Enums<"notificationType"> | null;
	email: Enums<"emailType"> | null;
	isExternal: boolean;
}

/**
 * Every kind of notification the application sends, in the order a preferences screen lists them.
 *
 * This list is the source of truth: `Notifications.notifyUsers` takes a kind and reads its push and
 * mail types from here, and every preferences screen renders its rows from here. A kind added to
 * the `notification_kind` enum without a row fails `notification-kinds.test.ts`.
 */
export const NOTIFICATION_KINDS = [
	{
		key: "note-mention",
		group: "notes",
		label: "notifications.kinds.note_mention",
		description: "notifications.kinds.note_mention_description",
		push: "note",
		email: "note-mention",
		isExternal: false,
	},
	{
		key: "protocol-involvement",
		group: "protocols",
		label: "notifications.kinds.protocol_involvement",
		description: "notifications.kinds.protocol_involvement_description",
		push: "protocol",
		email: "protocol-involvement",
		isExternal: true,
	},
	{
		key: "project-assigned",
		group: "projects",
		label: "notifications.kinds.project_assigned",
		description: "notifications.kinds.project_assigned_description",
		push: "project",
		email: "project-assigned",
		isExternal: false,
	},
	{
		key: "project-number-changed",
		group: "projects",
		label: "notifications.kinds.project_number_changed",
		description: "notifications.kinds.project_number_changed_description",
		push: "project",
		email: "project-number-changed",
		isExternal: false,
	},
	{
		key: "order-complete",
		group: "orders",
		label: "notifications.kinds.order_complete",
		description: "notifications.kinds.order_complete_description",
		push: "order",
		email: "order-complete",
		isExternal: false,
	},
	{
		key: "plan-published",
		group: "planning",
		label: "notifications.kinds.plan_published",
		description: "notifications.kinds.plan_published_description",
		push: "plan",
		email: null,
		isExternal: false,
	},
	{
		key: "attendance-decided",
		group: "attendances",
		label: "notifications.kinds.attendance_decided",
		description: "notifications.kinds.attendance_decided_description",
		push: "attendance",
		email: null,
		isExternal: false,
	},
	{
		key: "substitution-assigned",
		group: "substitutions",
		label: "notifications.kinds.substitution_assigned",
		description: "notifications.kinds.substitution_assigned_description",
		push: "substitution",
		email: "substitution-internal-note",
		isExternal: false,
	},
	{
		key: "substitution-team-note",
		group: "substitutions",
		label: "notifications.kinds.substitution_team_note",
		description: "notifications.kinds.substitution_team_note_description",
		push: null,
		email: "substitution-team-note",
		isExternal: false,
	},
	{
		key: "substitution-external-note",
		group: "substitutions",
		label: "notifications.kinds.substitution_external_note",
		description: "notifications.kinds.substitution_external_note_description",
		push: null,
		email: "substitution-external-note",
		isExternal: true,
	},
	{
		key: "partner-report",
		group: "partners",
		label: "notifications.kinds.partner_report",
		description: "notifications.kinds.partner_report_description",
		push: null,
		email: "partner-report",
		isExternal: true,
	},
	{
		key: "partnership-requested",
		group: "partners",
		label: "notifications.kinds.partnership_requested",
		description: "notifications.kinds.partnership_requested_description",
		push: "partner",
		email: "partnership-requested",
		isExternal: false,
	},
	{
		key: "partnership-activated",
		group: "partners",
		label: "notifications.kinds.partnership_activated",
		description: "notifications.kinds.partnership_activated_description",
		push: "partner",
		email: "partnership-activated",
		isExternal: false,
	},
	{
		key: "partnership-deactivated",
		group: "partners",
		label: "notifications.kinds.partnership_deactivated",
		description: "notifications.kinds.partnership_deactivated_description",
		push: "partner",
		email: "partnership-deactivated",
		isExternal: false,
	},
	{
		key: "incoming-invoice-review-requested",
		group: "incoming_invoices",
		label: "notifications.kinds.incoming_invoice_review_requested",
		description: "notifications.kinds.incoming_invoice_review_requested_description",
		push: "incoming-invoice",
		email: "incoming-invoice-review-requested",
		isExternal: false,
	},
	{
		key: "incoming-invoice-review-decided",
		group: "incoming_invoices",
		label: "notifications.kinds.incoming_invoice_review_decided",
		description: "notifications.kinds.incoming_invoice_review_decided_description",
		push: "incoming-invoice",
		email: "incoming-invoice-review-decided",
		isExternal: false,
	},
] as const satisfies readonly NotificationKind[];

/**
 * Look up one kind by its key.
 *
 * @param key - The `notification_kind` value.
 * @returns The catalogue row; every enum value has one, which the catalogue test pins.
 */
export function getNotificationKind(key: Enums<"notificationKind">): NotificationKind {
	const kind = NOTIFICATION_KINDS.find((entry) => entry.key === key);
	if (!kind) throw new Error(`Unknown notification kind: ${key}`);
	return kind;
}

/**
 * The kinds that can arrive by e-mail, which is what a mail preferences page offers.
 *
 * @param options.external - `true` for the kinds an address outside the organisation can
 *   receive, `false` for the ones only users receive; left out, every kind with a mail.
 * @returns The catalogue rows with a non-null `email`, in catalogue order.
 */
export function getEmailKinds(options: { external?: boolean } = {}) {
	return NOTIFICATION_KINDS.filter(
		(kind): kind is Extract<(typeof NOTIFICATION_KINDS)[number], { email: string }> =>
			kind.email !== null &&
			(options.external === undefined || kind.isExternal === options.external),
	);
}

/**
 * The kind a mail type belongs to, for a mail that knows only its `email_type`.
 *
 * @param type - The `email_type` of a mail.
 * @returns The catalogue row sending that mail, or null for a mail no kind sends — the account
 *   mails (`verify-email`, `reset-password`) and the types no handler sends any more.
 */
export function getKindForEmailType(type: Enums<"emailType">): NotificationKind | null {
	return NOTIFICATION_KINDS.find((kind) => kind.email === type) ?? null;
}

/** A channel a notification can reach a person on. */
export type NotificationChannel = "push" | "email";

/** A person's choice for one kind: each channel on or off, a channel left out unchanged. */
export type NotificationChannelChoice = Partial<Record<NotificationChannel, boolean>>;

/**
 * The choices in a preferences write that name a channel the kind is never sent on.
 *
 * Such a choice could be stored — a push opt-out for a mail-only kind is a valid row — but it
 * would describe nothing, and a screen that sent it is out of step with the catalogue. The write
 * schema refuses a body with any, and so does the handler behind it.
 *
 * @param preferences - The choices per kind, as a preferences write carries them.
 * @returns One entry per kind and channel the catalogue does not offer; empty when all are offered.
 */
export function getUnofferedChannels(
	preferences: Partial<Record<Enums<"notificationKind">, NotificationChannelChoice>>,
): Array<{ kind: Enums<"notificationKind">; channel: NotificationChannel }> {
	return Object.entries(preferences).flatMap(([key, choice]) => {
		const kind = getNotificationKind(key as Enums<"notificationKind">);
		return (["push", "email"] as const)
			.filter((channel) => choice?.[channel] !== undefined && kind[channel] === null)
			.map((channel) => ({ kind: kind.key, channel }));
	});
}
